import { useTranslation } from '@rhino-project/core/i18n';
import { createContext, ReactNode, use, useState } from 'react';
import { cn } from '@heroui/react';
import { useGlobalComponent } from '@rhino-project/core/hooks';

interface ApplicationShellContextValue {
  /** Whether the mobile nav menu is expanded (below the persistent-sidebar breakpoint). */
  isMenuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
  /**
   * Full-bleed layout: drop the persistent sidebar column so the main content goes
   * edge-to-edge. Navigation stays reachable through the navbar's own compact mode.
   */
  compact: boolean;
}

const ApplicationShellContext = createContext<ApplicationShellContextValue>({
  isMenuOpen: false,
  setMenuOpen: () => {},
  compact: false
});

/**
 * Read the shell's shared layout state from any descendant of `ApplicationShell`.
 * Lets a slotted navbar coordinate its compact/menu state with the shell.
 */
export const useApplicationShell = () => use(ApplicationShellContext);

export interface ApplicationShellProps {
  children?: ReactNode;
  compact?: boolean;
  [key: string]: unknown;
}

export const ApplicationShellBase = ({
  children,
  compact = false
}: ApplicationShellProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <ApplicationShellContext
      value={{ isMenuOpen, setMenuOpen: setIsMenuOpen, compact }}
    >
      <div
        className={
          compact
            ? 'h-dvh flex flex-col'
            : 'h-dvh flex flex-col xl:grid xl:grid-cols-[256px_1fr]'
        }
      >
        {children}
      </div>
    </ApplicationShellContext>
  );
};

interface ShellSlotProps {
  children?: ReactNode;
}

/** The sidebar column: bordered, sticky, holds the nav region and secondary footer. */
const ApplicationShellSidebar = ({ children }: ShellSlotProps) => {
  const { t } = useTranslation();
  const { compact, isMenuOpen, setMenuOpen } = useApplicationShell();

  return (
    <div
      className={
        compact
          ? 'overflow-hidden flex flex-col border-b border-border'
          : 'xl:h-screen xl:sticky xl:top-0 xl:bg-surface overflow-hidden flex flex-col border-r border-border'
      }
    >
      <div className={compact ? 'p-4' : 'p-4 xl:hidden'}>
        <button
          type="button"
          className="p-2"
          aria-label={t('rhino.frontend.messages.toggle_menu')}
          aria-expanded={isMenuOpen}
          onClick={() => setMenuOpen(!isMenuOpen)}
        >
          <svg
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            {isMenuOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>
      </div>
      {children}
    </div>
  );
};

/** The scrollable primary-navigation region inside the sidebar column. */
const ApplicationShellNav = ({ children }: ShellSlotProps) => {
  const { isMenuOpen, compact } = useApplicationShell();
  const visibility = isMenuOpen ? '' : compact ? 'hidden' : 'hidden xl:block';

  return (
    <div className={cn('flex-1 overflow-y-auto', visibility)}>{children}</div>
  );
};

/**
 * The pinned footer block at the bottom of the sidebar column. Hidden on mobile
 * unless the nav menu is open; always shown at the persistent-sidebar breakpoint.
 */
const ApplicationShellSecondary = ({ children }: ShellSlotProps) => {
  const { isMenuOpen, compact } = useApplicationShell();

  if (!children) return null;

  return (
    <div
      className={cn(
        'p-4 border-t border-border',
        !compact && 'xl:bg-surface',
        !isMenuOpen && (compact ? 'hidden' : 'hidden xl:block')
      )}
    >
      {children}
    </div>
  );
};

/** The main content region. */
const ApplicationShellMain = ({ children }: ShellSlotProps) => (
  <main className="flex-1 p-6 bg-background overflow-auto flex flex-col">
    {children}
  </main>
);

export const ApplicationShell = (props: ApplicationShellProps) =>
  useGlobalComponent('ApplicationShell', ApplicationShellBase, props);

ApplicationShell.displayName = 'ApplicationShell';
ApplicationShell.Sidebar = ApplicationShellSidebar;
ApplicationShell.Nav = ApplicationShellNav;
ApplicationShell.Secondary = ApplicationShellSecondary;
ApplicationShell.Main = ApplicationShellMain;
